// NodeForm.jsx

// NodeForm.jsx

import React, { useCallback, useEffect } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { coerceFromInput } from 'utilities/NodeFieldCoercion';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';

// Hooks
import useBaseAccess from 'hooks/useBaseAccess';
import useFieldModalController from 'node/form/hooks/useFieldModalController';
// import useFieldReorder from 'node/form/hooks/useFieldReorder';
import useNodeFieldChange from 'node/form/hooks/useNodeFieldChange';
import useNodeFormFields from 'node/form/hooks/useNodeFormFields';
import useNodeTypeDefinition from 'node/form/hooks/useNodeTypeDefinition';
import useNodeTypeFieldMutations from 'node/form/hooks/useNodeTypeFieldMutations';
import useNodeTypeSchemaCacheRefresh from 'node/form/hooks/useNodeTypeSchemaCacheRefresh';
import useOverrideFields from 'node/form/hooks/useOverrideFields';

// Constants
import { FIELD_MESSAGES } from 'node/form/constants/fieldMessages';

// CSS
import './NodeForm.css';

// Components
import SidebarBucketAddButton from 'main/sidebar/buckets/addbutton/SidebarBucketAddButton';
import FieldModal from 'node/form/fieldmodal/FieldModal';
import NodeFormFieldsList from 'node/form/NodeFormFieldsList';

/**
 * NodeForm
 *
 * Renders schema-driven fields for a node.
 * - fields           -> node.data_{source}[field.name]
 *
 * @param {object} node - Active node record.
 * @param {function} onChange - Called with node patch updates.
 * @param {function} onCoverSaved - Callback when cover media is saved.
 * @param {function} onLocationSaved - Callback when location is saved.
 * @param {function} onSubtitleSaved - Callback when description is saved.
 * @param {function} onTagsSaved - Callback when tags are saved.
 * @param {string[]} [excludedFieldTypes] - Field types to omit from the rendered form.
 * @param {boolean} [autoFocusRichText=false] - Autofocus the first richtext field.
 * @param {string|number} [autoFocusKey] - Refocus token for the autofocused richtext field.
 *
 * @returns {ReactNode} The rendered properties form.
 */
const NodeForm = ({
    node,
    onChange,
    onCoverSaved,
    onLocationSaved,
    onSubtitleSaved,
    onTagsSaved,
    excludedFieldTypes,
    readOnly = false,
    autoFocusRichText = false,
    autoFocusKey = '',
}) => {

    // ----- CONTEXT ----- //

    // Language Context

    const language = useCurrentLanguage();

    // Node Types Context
    const { getType, getLabel, getRevision } = useNodeTypes();

    // ----- HOOKS ----- //

    const { can, isRoleLoading } = useBaseAccess();

    const nodeTypeKey = String(node?.type || '');

    const nodeTypesRevision = getRevision();

    // Resolves the active node type definition.
    const { typeDef, loading } = useNodeTypeDefinition(nodeTypeKey, getType, nodeTypesRevision);

    const isSystemType = typeDef?.system === true;
    const canAddTypeFields = !isSystemType && !isRoleLoading && can('types.fields.create');
    const canManageTypeSchema = !isSystemType && !isRoleLoading && can('types.update');

    const resolvedTypeKey = typeDef?.key ? String(typeDef.key).trim() : nodeTypeKey;
    const resolvedBaseKey = typeof node?.baseKey === 'string' && node.baseKey.trim()
        ? node.baseKey.trim()
        : (typeof typeDef?.baseKey === 'string' ? typeDef.baseKey : null);

    // Resolves base-specific field override state.
    const { overrideFields, setOverrideFields } = useOverrideFields({
        nodeTypeKey,
        typeDef,
    });

    // Builds current field lists for rendering and modal editing.
    const { fields, resolvedFields } = useNodeFormFields({
        nodeTypeKey,
        language,
        typeDef,
        overrideFields,
        excludedFieldTypes,
        node,
    });

    const refreshNodeTypesCache = useNodeTypeSchemaCacheRefresh();

    // ----- HANDLERS ----- //

    // Handles direct field change updates.

    const handleFieldChange = useNodeFieldChange({
        node,
        nodeTypeKey,
        onChange,
    });

    // ----- HOOKS ----- //

    // Field reorder on the live form is paused — schema order edits live in
    // collection settings. Keep useFieldReorder wired below for a quick restore.
    //
    // const {
    //     draggingFieldName,
    //     dragOverFieldName,
    //     handleDragStart,
    //     handleDragOver,
    //     handleDrop,
    //     handleDragEnd,
    // } = useFieldReorder({
    //     resolvedFields,
    //     fields,
    //     node,
    //     nodeTypeKey,
    //     resolvedTypeKey,
    //     resolvedBaseKey,
    //     overrideFields,
    //     setOverrideFields,
    //     refreshNodeTypesCache,
    //     isDisabled: !canManageTypeSchema,
    // });
    const draggingFieldName = null;
    const dragOverFieldName = null;
    const handleDragStart = undefined;
    const handleDragOver = undefined;
    const handleDrop = undefined;
    const handleDragEnd = undefined;

    // Controls add/edit field modal state and actions.
    const {
        fieldModalOpen,
        fieldInsertIndex,
        fieldEditingField,
        handleFieldModalOpen,
        handleAddFieldClick: handleAddFieldAtIndex,
        handleSettingsClick,
    } = useFieldModalController({
        canAddFields: canAddTypeFields,
        canManageFields: canManageTypeSchema,
    });

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (canAddTypeFields || canManageTypeSchema) return;
        if (!fieldModalOpen) return;
        handleFieldModalOpen(false);
    }, [canAddTypeFields, canManageTypeSchema, fieldModalOpen, handleFieldModalOpen]);

    // ----- HANDLERS ----- //

    /**
     * Opens add-field modal at the selected slot index.
     *
     * @param {string} _groupName - Group name placeholder.
     * @param {number} index - Field insert index.
     */

    const handleAddFieldClick = useCallback((_groupName, index) => {
        handleAddFieldAtIndex(index);
    }, [handleAddFieldAtIndex]);

    // ----- MEMOS ----- //

    /**
     * Applies a field default value to the current node when supported.
     *
     * @param {object} field - Field definition.
     * @param {*} defaultValue - Field default value.
     */

    const applyDefaultToNode = useCallback((field, defaultValue) => {
        if (!onChange || !node?.key) return;
        if (!field || defaultValue === undefined || defaultValue === null) return;
        if (field.type === 'tags' || field.type === 'dates' || field.type === 'location' || field.type === 'checklist') return;

        if (field.type !== 'boolean' && defaultValue === '') return;

        const value = coerceFromInput(defaultValue, field.type, field);
        const patch = buildNodeFieldPatch(node, field, nodeTypeKey, value);
        if (!patch) return;

        onChange(node.key, patch);
    }, [node, nodeTypeKey, onChange]);

    // ----- HOOKS ----- //

    // Handles node type field mutation actions.

    const {
        fieldSaving,
        handleFieldSave,
        handleFieldUpdate,
        handleFieldDelete,
    } = useNodeTypeFieldMutations({
        isDisabled: fieldEditingField ? !canManageTypeSchema : !canAddTypeFields,
        fieldEditingField,
        fieldInsertIndex,
        fields,
        resolvedFields,
        overrideFields,
        setOverrideFields,
        resolvedTypeKey,
        resolvedBaseKey,
        getNodeKey: () => node?.key || null,
        requireNodeKey: true,
        nodeNotReadyMessage: FIELD_MESSAGES.NODE_NOT_READY,
        typeNotReadyMessage: FIELD_MESSAGES.NODE_TYPE_NOT_READY,
        refreshNodeTypesCache,
        closeFieldModal: () => handleFieldModalOpen(false),
        applyDefaultToNode,
    });
    if (loading) return null;
    if (!typeDef) return null;

    const nodeTypeLabel = getLabel(nodeTypeKey);

    // ----- RENDER ----- //

    return (
        <div className="node-form-container">
            <NodeFormFieldsList
              fields={fields}
              node={node}
              nodeTypeKey={nodeTypeKey}
              language={language}
              getType={getType}
              canAddFields={!readOnly && canAddTypeFields}
              canManageFields={!readOnly && canManageTypeSchema}
              draggingFieldName={readOnly ? null : draggingFieldName}
              dragOverFieldName={readOnly ? null : dragOverFieldName}
              onAddField={readOnly ? undefined : handleAddFieldClick}
              onChange={readOnly ? undefined : onChange}
              onCoverSaved={readOnly ? undefined : onCoverSaved}
              onDragEnd={readOnly ? undefined : handleDragEnd}
              onDragOver={readOnly ? undefined : handleDragOver}
              onDragStart={readOnly ? undefined : handleDragStart}
              onDrop={readOnly ? undefined : handleDrop}
              onFieldChange={readOnly ? undefined : handleFieldChange}
              onLocationSaved={readOnly ? undefined : onLocationSaved}
              onSettingsClick={readOnly ? undefined : handleSettingsClick}
              onSubtitleSaved={readOnly ? undefined : onSubtitleSaved}
              onTagsSaved={readOnly ? undefined : onTagsSaved}
              previewMode={readOnly}
              autoFocusRichText={!readOnly && autoFocusRichText}
              autoFocusKey={autoFocusKey}
            />

            {/* FOOTER 
            {!readOnly && canAddTypeFields ? (

                <div className="node-form-footer">
                    <SidebarBucketAddButton
                      label="New Field"
                      onClick={() => handleAddFieldAtIndex(fields.length)}
                    />
                </div>
            ) : null}
            */}

            <FieldModal
              isOpen={fieldModalOpen}
              setOpen={handleFieldModalOpen}
              isSaving={fieldSaving}
              editingField={fieldEditingField}
              nodeTypeLabel={nodeTypeLabel}
              existingFields={resolvedFields}
              typeKey={resolvedTypeKey}
              baseKey={resolvedBaseKey}
              onDelete={canManageTypeSchema ? handleFieldDelete : undefined}
              onSave={fieldEditingField ? handleFieldUpdate : handleFieldSave}
            />
        </div>
    );
};

// Dev Tools
NodeForm.displayName = 'NodeForm';

export default NodeForm;
